import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function ServerMetrics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5);

  const fetchServerMetrics = useCallback(async () => {
    try {
      const response = await adminAPI.getServerMetrics();
      setData(response.data.data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch server metrics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServerMetrics();

    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchServerMetrics, refreshInterval * 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchServerMetrics, autoRefresh, refreshInterval]);

  const getStatusColor = (value, type) => {
    if (type === 'cpu' || type === 'memory') {
      if (value < 50) return '#10b981';
      if (value < 75) return '#f59e0b';
      return '#ef4444';
    }
    if (type === 'storage') {
      if (value < 70) return '#10b981';
      if (value < 85) return '#f59e0b';
      return '#ef4444';
    }
    if (type === 'status') {
      return value === 'online' ? '#10b981' : '#ef4444';
    }
    return '#6b7280';
  };

  // Calculate average storage usage from online servers
  const getAvgStorageUsage = () => {
    if (!data?.servers) return 0;
    const onlineServers = data.servers.filter(s => s.status === 'online' && s.metrics?.storage);
    if (onlineServers.length === 0) return 0;
    const total = onlineServers.reduce((sum, s) => sum + parseFloat(s.metrics.storage.usagePercent || 0), 0);
    return (total / onlineServers.length).toFixed(1);
  };

  const getLoadColor = (load, cores) => {
    const ratio = load / cores;
    if (ratio < 0.7) return '#10b981';
    if (ratio < 1) return '#f59e0b';
    return '#ef4444';
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const CircularProgress = ({ value, size = 120, strokeWidth = 10, color }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (value / 100) * circumference;

    return (
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          stroke="rgba(255, 255, 255, 0.1)"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          r={radius}
          cx={size / 2}
          cy={size / 2}
          style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dy=".3em"
          fill="white"
          fontSize="24"
          fontWeight="bold"
          style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}
        >
          {value}%
        </text>
      </svg>
    );
  };

  if (loading) {
    return (
      <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', minHeight: '100vh' }}>
        <Navbar />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <div style={{ color: 'white', fontSize: '24px' }}>Loading server metrics...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }}>
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            color: 'white',
            marginBottom: 'var(--space-sm)'
          }}>
            Server Metrics
          </h1>
          <p style={{ color: '#94a3b8', fontSize: 'var(--font-size-lg)' }}>
            Live CPU, Memory & Load monitoring for production servers
          </p>
          <div style={{ marginTop: 'var(--space-md)', display: 'flex', justifyContent: 'center', gap: 'var(--space-md)', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={fetchServerMetrics}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--primary)',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Refresh Now
            </button>
            <label style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh
            </label>
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.1)',
                color: 'white'
              }}
            >
              <option value={3}>3s</option>
              <option value={5}>5s</option>
              <option value={10}>10s</option>
              <option value={30}>30s</option>
            </select>
            {lastRefresh && (
              <span style={{ color: '#64748b', fontSize: '14px' }}>
                Last updated: {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid #ef4444',
            borderRadius: '12px',
            padding: 'var(--space-md)',
            marginBottom: 'var(--space-xl)',
            color: '#fca5a5'
          }}>
            Error: {error}
          </div>
        )}

        {/* Summary Cards */}
        {data?.summary && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--space-lg)',
            marginBottom: 'var(--space-xl)'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '16px',
              padding: 'var(--space-lg)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '36px', fontWeight: '700', color: '#10b981' }}>
                {data.summary.onlineServers}/{data.summary.totalServers}
              </div>
              <div style={{ color: '#94a3b8', marginTop: '8px' }}>Servers Online</div>
            </div>

            <div style={{
              background: `linear-gradient(135deg, ${getStatusColor(data.summary.avgCpuUsage, 'cpu')}33 0%, ${getStatusColor(data.summary.avgCpuUsage, 'cpu')}11 100%)`,
              border: `1px solid ${getStatusColor(data.summary.avgCpuUsage, 'cpu')}66`,
              borderRadius: '16px',
              padding: 'var(--space-lg)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '36px', fontWeight: '700', color: getStatusColor(data.summary.avgCpuUsage, 'cpu') }}>
                {data.summary.avgCpuUsage}%
              </div>
              <div style={{ color: '#94a3b8', marginTop: '8px' }}>Avg CPU Usage</div>
            </div>

            <div style={{
              background: `linear-gradient(135deg, ${getStatusColor(data.summary.avgMemoryUsage, 'memory')}33 0%, ${getStatusColor(data.summary.avgMemoryUsage, 'memory')}11 100%)`,
              border: `1px solid ${getStatusColor(data.summary.avgMemoryUsage, 'memory')}66`,
              borderRadius: '16px',
              padding: 'var(--space-lg)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '36px', fontWeight: '700', color: getStatusColor(data.summary.avgMemoryUsage, 'memory') }}>
                {data.summary.avgMemoryUsage}%
              </div>
              <div style={{ color: '#94a3b8', marginTop: '8px' }}>Avg Memory Usage</div>
            </div>

            <div style={{
              background: `linear-gradient(135deg, ${getStatusColor(parseFloat(getAvgStorageUsage()), 'storage')}33 0%, ${getStatusColor(parseFloat(getAvgStorageUsage()), 'storage')}11 100%)`,
              border: `1px solid ${getStatusColor(parseFloat(getAvgStorageUsage()), 'storage')}66`,
              borderRadius: '16px',
              padding: 'var(--space-lg)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '36px', fontWeight: '700', color: getStatusColor(parseFloat(getAvgStorageUsage()), 'storage') }}>
                {getAvgStorageUsage()}%
              </div>
              <div style={{ color: '#94a3b8', marginTop: '8px' }}>Avg Storage Usage</div>
            </div>
          </div>
        )}

        {/* Server Cards */}
        {data?.servers && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))',
            gap: 'var(--space-xl)'
          }}>
            {data.servers.map((server) => (
              <div
                key={server.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '20px',
                  padding: 'var(--space-xl)',
                  border: `2px solid ${server.status === 'online' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                }}
              >
                {/* Server Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
                  <div>
                    <h3 style={{ color: 'white', margin: 0, fontSize: '20px' }}>
                      {server.name}
                    </h3>
                    <div style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
                      {server.ip}
                    </div>
                  </div>
                  <div style={{
                    padding: '6px 16px',
                    borderRadius: '20px',
                    background: getStatusColor(server.status, 'status'),
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    {server.status === 'online' ? 'Online' : 'Offline'}
                  </div>
                </div>

                {server.status === 'online' && server.metrics ? (
                  <>
                    {/* CPU & Memory Gauges */}
                    <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 'var(--space-lg)' }}>
                      <div style={{ textAlign: 'center' }}>
                        <CircularProgress
                          value={server.metrics.cpu.usage}
                          color={getStatusColor(server.metrics.cpu.usage, 'cpu')}
                        />
                        <div style={{ color: '#94a3b8', marginTop: '8px' }}>CPU Usage</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <CircularProgress
                          value={parseFloat(server.metrics.memory.usagePercent)}
                          color={getStatusColor(parseFloat(server.metrics.memory.usagePercent), 'memory')}
                        />
                        <div style={{ color: '#94a3b8', marginTop: '8px' }}>Memory Usage</div>
                      </div>
                    </div>

                    {/* Detailed Stats */}
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '12px',
                      padding: 'var(--space-md)'
                    }}>
                      {/* CPU Details */}
                      <div style={{ marginBottom: 'var(--space-md)' }}>
                        <div style={{ color: '#60a5fa', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                          CPU Details
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                          <div style={{ color: '#94a3b8' }}>Cores: <span style={{ color: 'white' }}>{server.metrics.cpu.cores}</span></div>
                          <div style={{ color: '#94a3b8' }}>Model: <span style={{ color: 'white', fontSize: '11px' }}>{server.metrics.cpu.model.split(' ')[0]}</span></div>
                        </div>
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Load Average</div>
                          <div style={{ display: 'flex', gap: '12px' }}>
                            {['1min', '5min', '15min'].map((period) => (
                              <div key={period} style={{ textAlign: 'center' }}>
                                <div style={{
                                  color: getLoadColor(parseFloat(server.metrics.cpu.loadAvg[period]), server.metrics.cpu.cores),
                                  fontWeight: '600'
                                }}>
                                  {server.metrics.cpu.loadAvg[period]}
                                </div>
                                <div style={{ color: '#64748b', fontSize: '11px' }}>{period}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Memory Details */}
                      <div style={{ marginBottom: 'var(--space-md)' }}>
                        <div style={{ color: '#a78bfa', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                          Memory Details
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', fontSize: '13px' }}>
                          <div>
                            <div style={{ color: '#64748b', fontSize: '11px' }}>Total</div>
                            <div style={{ color: 'white' }}>{server.metrics.memory.totalFormatted}</div>
                          </div>
                          <div>
                            <div style={{ color: '#64748b', fontSize: '11px' }}>Used</div>
                            <div style={{ color: '#f59e0b' }}>{server.metrics.memory.usedFormatted}</div>
                          </div>
                          <div>
                            <div style={{ color: '#64748b', fontSize: '11px' }}>Free</div>
                            <div style={{ color: '#10b981' }}>{server.metrics.memory.freeFormatted}</div>
                          </div>
                        </div>
                        {/* Memory Bar */}
                        <div style={{
                          marginTop: '8px',
                          height: '8px',
                          background: 'rgba(255,255,255,0.1)',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${server.metrics.memory.usagePercent}%`,
                            height: '100%',
                            background: getStatusColor(parseFloat(server.metrics.memory.usagePercent), 'memory'),
                            borderRadius: '4px',
                            transition: 'width 0.5s ease-in-out'
                          }} />
                        </div>
                      </div>

                      {/* Storage Details */}
                      {server.metrics.storage && (
                        <div style={{ marginBottom: 'var(--space-md)' }}>
                          <div style={{ color: '#fb923c', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                            💾 Storage Details
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', fontSize: '13px' }}>
                            <div>
                              <div style={{ color: '#64748b', fontSize: '11px' }}>Total</div>
                              <div style={{ color: 'white' }}>{server.metrics.storage.totalFormatted}</div>
                            </div>
                            <div>
                              <div style={{ color: '#64748b', fontSize: '11px' }}>Used</div>
                              <div style={{ color: '#f59e0b' }}>{server.metrics.storage.usedFormatted}</div>
                            </div>
                            <div>
                              <div style={{ color: '#64748b', fontSize: '11px' }}>Available</div>
                              <div style={{ color: '#10b981' }}>{server.metrics.storage.availableFormatted}</div>
                            </div>
                          </div>
                          {/* Storage Bar */}
                          <div style={{
                            marginTop: '8px',
                            height: '8px',
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${server.metrics.storage.usagePercent}%`,
                              height: '100%',
                              background: getStatusColor(parseFloat(server.metrics.storage.usagePercent), 'storage'),
                              borderRadius: '4px',
                              transition: 'width 0.5s ease-in-out'
                            }} />
                          </div>
                          <div style={{ textAlign: 'right', fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                            {server.metrics.storage.usagePercent}% used
                          </div>
                        </div>
                      )}

                      {/* System Info */}
                      <div>
                        <div style={{ color: '#34d399', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                          System Info
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                          <div style={{ color: '#94a3b8' }}>Uptime: <span style={{ color: '#10b981' }}>{formatUptime(server.metrics.uptime)}</span></div>
                          <div style={{ color: '#94a3b8' }}>Platform: <span style={{ color: 'white' }}>{server.metrics.platform}</span></div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: 'var(--space-xl)',
                    color: '#ef4444'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>Server Offline</div>
                    <div style={{ color: '#94a3b8', fontSize: '14px' }}>{server.error || 'Unable to connect'}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        <div style={{
          background: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '12px',
          padding: 'var(--space-lg)',
          marginTop: 'var(--space-xl)',
          color: '#93c5fd'
        }}>
          <strong>Understanding the Metrics:</strong>
          <ul style={{ margin: '8px 0 0 20px', lineHeight: '1.8' }}>
            <li><strong>CPU Usage:</strong> Current processor utilization. Below 50% is healthy (green), 50-75% is moderate (yellow), above 75% needs attention (red).</li>
            <li><strong>Memory Usage:</strong> RAM utilization percentage. Same color thresholds as CPU.</li>
            <li><strong>Storage Usage:</strong> Disk space utilization. Below 70% is healthy (green), 70-85% is moderate (yellow), above 85% needs attention (red).</li>
            <li><strong>Load Average:</strong> Number of processes waiting for CPU over 1, 5, and 15 minutes. Should ideally be below the number of cores.</li>
            <li><strong>Uptime:</strong> Time since last server restart.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ServerMetrics;
